import React, { useEffect, useState } from "react";
import { fetchCurrentUser, toggleRedactUnmappedData } from "../../../../api-client/APIClient";
import useAppContext from "../../../context/useAppContext";
import { ButtonVariant, Checkbox, Dialog, Text } from "@merge-api/merge-javascript-shared";

type Props = {
  isRedactionCurrentlyEnabledForOrg: boolean;
  organizationId: string;
  isModalOpen: boolean;
  onHide: () => void;
  onConfirmResponse: () => void;
};

const RedactUnmappedDataModal = ({
  isRedactionCurrentlyEnabledForOrg,
  organizationId,
  isModalOpen,
  onHide,
  onConfirmResponse,
}: Props) => {
  const { setUser } = useAppContext();
  const [isToggling, setIsToggling] = useState(false);
  const [isConfirmationChecked, setIsConfirmationChecked] = useState(false);

  // Reset the consent checkbox each time the modal opens
  useEffect(() => {
    if (isModalOpen) setIsConfirmationChecked(false);
  }, [isModalOpen]);

  const _onConfirmResponse = () => {
    // Once the setting is toggled, we need to refetch the user to get the updated redaction status.
    // Else, the data in the AppContext's user will go stale until the user is refetched (e.g. via a page refresh).
    fetchCurrentUser(setUser);

    setIsToggling(false);
    onConfirmResponse();
  };

  const _onConfirmError = () => {
    setIsToggling(false);
    onConfirmResponse();
  };

  return (
    <Dialog
      open={isModalOpen}
      onClose={onHide}
      onPrimaryButtonClick={() => {
        setIsToggling(true);
        toggleRedactUnmappedData(
          !isRedactionCurrentlyEnabledForOrg,
          organizationId,
          undefined,
          _onConfirmResponse,
          _onConfirmError,
        );
      }}
      primaryButtonVariant={ButtonVariant.DangerFilled}
      primaryButtonText="Confirm"
      primaryButtonLoading={isToggling}
      primaryButtonDisabled={!isConfirmationChecked}
      onSecondaryButtonClick={onHide}
      secondaryButtonText="Cancel"
      title={
        isRedactionCurrentlyEnabledForOrg
          ? "Disable redacting unmapped data"
          : "Enable redacting unmapped data"
      }
      variant="md"
    >
      <div className="flex flex-col gap-y-3">
        <Text>This change applies to all existing and new Linked Accounts going forward.</Text>
        <Text>Any individual Linked Account redaction settings will be removed.</Text>
        <Checkbox
          name="redact-unmapped-data-confirmation-checkbox"
          checked={isConfirmationChecked}
          onChange={(checked) => setIsConfirmationChecked(checked)}
          label="I understand that this changes what data Merge stores for my organization"
          labelFontWeight="semibold"
          labelAlignment="start"
        />
      </div>
    </Dialog>
  );
};

export default RedactUnmappedDataModal;
